/**
 * RTE-X11.5g — unified route direction + stop presentation (read model / UI only).
 * RTE-X11.5g-r1 — structured city only (warehouse / route cities / stop.city / addressDisplayFallback).
 */

import { normalizeCityLabel } from '../../utils/addressUtils';
import { displayCityWithAddressFallback } from '../../components/routes/routeDetails/addressDisplayFallback';

export const ROUTE_STOP_KIND_LABELS = {
  loading: 'Погрузка',
  unloading: 'Выгрузка'
} as const;

export const ROUTE_POINT_TYPE_LABELS = {
  warehouse: 'Склад',
  consignee: 'Получатель',
  application: 'Заявка',
  shipper: 'Отправитель'
} as const;

export type RouteCityResolutionContext = {
  routeLoadingCity?: string | null;
  routeUnloadingCity?: string | null;
  loadingWarehouse?: { id?: number; city?: string | null; name?: string | null } | null;
  unloadingWarehouse?: { id?: number; city?: string | null; name?: string | null } | null;
  minStopPosition?: number;
  maxStopPosition?: number;
};

export type RouteDirectionStop = {
  city?: string | null;
  address?: string | null;
  position?: number | null;
  type?: string | null;
  pointType?: string | null;
  pointId?: number | null;
  warehouse?: { city?: string | null; name?: string | null } | null;
};

export type RouteStopPresentationInput = {
  id?: number | null;
  type?: string | null;
  pointType?: string | null;
  pointId?: number | null;
  city?: string | null;
  address?: string | null;
  position?: number | null;
  contactPerson?: string | null;
  contractor?: { name?: string | null } | null;
  warehouse?: { name?: string | null; city?: string | null } | null;
};

export function buildRouteStopPresentationContext(params: {
  applications?: RouteStopPresentationContext['applications'];
  loadingWarehouse?: RouteStopPresentationContext['loadingWarehouse'];
  unloadingWarehouse?: RouteStopPresentationContext['unloadingWarehouse'];
  routeConsigneeName?: string | null;
  routeLoadingCity?: string | null;
  routeUnloadingCity?: string | null;
  minStopPosition?: number;
  maxStopPosition?: number;
}): RouteStopPresentationContext {
  return {
    applications: params.applications,
    loadingWarehouse: params.loadingWarehouse ?? null,
    unloadingWarehouse: params.unloadingWarehouse ?? null,
    routeConsigneeName: params.routeConsigneeName ?? null,
    routeLoadingCity: params.routeLoadingCity ?? null,
    routeUnloadingCity: params.routeUnloadingCity ?? null,
    minStopPosition: params.minStopPosition,
    maxStopPosition: params.maxStopPosition
  };
}

export type RouteStopPresentationContext = RouteCityResolutionContext & {
  applications?: Array<{
    applicationId?: number | null;
    application?: {
      id?: number | null;
      internalNumber?: string | null;
      client?: { name?: string | null } | null;
      consignee?: { name?: string | null } | null;
    } | null;
  }>;
  routeConsigneeName?: string | null;
};

export type RouteStopPresentation = {
  stopType: 'loading' | 'unloading' | 'other';
  pointType: string | null;
  ownerType: string | null;
  ownerName: string | null;
  applicationNumber: string | null;
  city: string | null;
  displayCity: string;
  destinationLabel: string;
  primaryLine: string;
  secondaryLine: string | null;
  sourceLabel: string;
};

export const buildStopPresentation = buildRouteStopPresentation;

function normPointType(value: unknown): string | null {
  const pt = String(value ?? '').trim().toLowerCase();
  return pt || null;
}

function warehouseCityForStop(
  stop: Pick<RouteDirectionStop, 'pointType' | 'pointId' | 'warehouse'>,
  ctx: RouteCityResolutionContext
): string | null {
  if (normPointType(stop.pointType) !== 'warehouse') return null;
  const fromStop = normalizeCityLabel(stop.warehouse?.city ?? null);
  if (fromStop) return fromStop;
  const pointId = stop.pointId != null ? Number(stop.pointId) : NaN;
  if (!Number.isFinite(pointId)) return null;
  const tryWh = (wh: { id?: number; city?: string | null } | null | undefined) => {
    if (!wh || Number(wh.id) !== pointId) return null;
    return normalizeCityLabel(wh.city ?? null);
  };
  return tryWh(ctx.loadingWarehouse) || tryWh(ctx.unloadingWarehouse);
}

function stopPositionValue(stop: { position?: number | null }, fallback: number): number {
  return stop.position != null && Number.isFinite(Number(stop.position))
    ? Number(stop.position)
    : fallback;
}

/**
 * Город точки: warehouse.city → stop.city / address fallback → route aggregate (только крайние, последний fallback).
 * RTE-X11.5i: route.loadingCity / route.unloadingCity не имеют приоритета над stop.city.
 */
export function resolveStructuredStopCity(
  stop: RouteDirectionStop,
  ctx: RouteCityResolutionContext = {}
): string {
  const whCity = warehouseCityForStop(stop, ctx);
  if (whCity) return whCity;

  const fromStructured = displayCityWithAddressFallback(stop.city, stop.address);
  if (fromStructured !== '—') return fromStructured;

  const position = stopPositionValue(stop, 0);
  const minPos = ctx.minStopPosition ?? position;
  const maxPos = ctx.maxStopPosition ?? position;
  const stopType = String(stop.type ?? '').toLowerCase();
  const routeLoad = normalizeCityLabel(ctx.routeLoadingCity);
  const routeUnload = normalizeCityLabel(ctx.routeUnloadingCity);

  if (stopType === 'loading' && position === minPos && routeLoad) {
    return routeLoad;
  }
  if (stopType === 'unloading' && position === maxPos && routeUnload) {
    return routeUnload;
  }

  return '—';
}

function resolveCitySegment(
  stop: RouteDirectionStop | undefined,
  ctx: RouteCityResolutionContext
): string {
  if (!stop) return 'Не указано';
  const label = resolveStructuredStopCity(stop, ctx);
  return label === '—' ? 'Не указано' : label;
}

function computePositionBounds(
  stops: Array<{ position?: number | null }>
): { min: number; max: number } {
  if (!stops.length) return { min: 0, max: 0 };
  const positions = stops.map((s, i) => stopPositionValue(s, i));
  return { min: Math.min(...positions), max: Math.max(...positions) };
}

export function buildRouteStopPresentationIndex(
  structureStops: Array<RouteStopPresentationInput & { id?: number | null }>,
  ctx: RouteStopPresentationContext = {}
): Map<number, RouteStopPresentation> {
  const bounds = computePositionBounds(structureStops);
  const enrichedCtx: RouteStopPresentationContext = {
    ...ctx,
    minStopPosition: ctx.minStopPosition ?? bounds.min,
    maxStopPosition: ctx.maxStopPosition ?? bounds.max
  };
  const map = new Map<number, RouteStopPresentation>();
  for (const row of structureStops) {
    const id = Number(row.id);
    if (!Number.isFinite(id)) continue;
    map.set(id, buildRouteStopPresentation(row, enrichedCtx));
  }
  return map;
}

export function sortStopsForDirection<T extends { position?: number | null }>(stops: T[]): T[] {
  return [...stops].sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
}

export function buildRouteDirectionLabel(
  stops: RouteDirectionStop[] | null | undefined,
  ctx: RouteCityResolutionContext = {}
): string {
  if (!stops?.length) return 'Не указано';
  const ordered = sortStopsForDirection(stops);
  const bounds = computePositionBounds(ordered);
  const cityCtx: RouteCityResolutionContext = {
    ...ctx,
    minStopPosition: ctx.minStopPosition ?? bounds.min,
    maxStopPosition: ctx.maxStopPosition ?? bounds.max
  };
  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  return `${resolveCitySegment(first, cityCtx)} → ${resolveCitySegment(last, cityCtx)}`;
}

export function extractRouteDirectionEndpoints(
  stops: RouteDirectionStop[] | null | undefined,
  ctx: RouteCityResolutionContext = {}
): {
  fromCity: string;
  toCity: string;
  label: string;
} {
  if (!stops?.length) {
    return { fromCity: 'Не указано', toCity: 'Не указано', label: 'Не указано' };
  }
  const ordered = sortStopsForDirection(stops);
  const bounds = computePositionBounds(ordered);
  const cityCtx: RouteCityResolutionContext = {
    ...ctx,
    minStopPosition: ctx.minStopPosition ?? bounds.min,
    maxStopPosition: ctx.maxStopPosition ?? bounds.max
  };
  const fromCity = resolveCitySegment(ordered[0], cityCtx);
  const toCity = resolveCitySegment(ordered[ordered.length - 1], cityCtx);
  return { fromCity, toCity, label: `${fromCity} → ${toCity}` };
}

function findApplication(
  ctx: RouteStopPresentationContext,
  applicationId: number
): { internalNumber?: string | null; client?: { name?: string | null } | null; consignee?: { name?: string | null } | null } | null {
  for (const row of ctx.applications ?? []) {
    const app = row.application;
    const aid =
      row.applicationId != null
        ? Number(row.applicationId)
        : app?.id != null
          ? Number(app.id)
          : NaN;
    if (Number.isFinite(aid) && aid === applicationId) return app ?? null;
  }
  return null;
}

function resolveWarehouseName(
  ctx: RouteStopPresentationContext,
  pointId: number
): string | null {
  const tryWh = (wh: { id?: number; name?: string | null } | null | undefined) => {
    if (!wh || Number(wh.id) !== pointId) return null;
    const name = wh.name != null ? String(wh.name).trim() : '';
    return name || null;
  };
  return tryWh(ctx.loadingWarehouse) || tryWh(ctx.unloadingWarehouse);
}

export function buildRouteStopPresentation(
  stop: RouteStopPresentationInput,
  ctx: RouteStopPresentationContext = {}
): RouteStopPresentation {
  const stopTypeRaw = String(stop.type ?? '').toLowerCase();
  const stopType: RouteStopPresentation['stopType'] =
    stopTypeRaw === 'loading' ? 'loading' : stopTypeRaw === 'unloading' ? 'unloading' : 'other';

  const pointType = normPointType(stop.pointType);
  const pointId = stop.pointId != null && Number.isFinite(Number(stop.pointId)) ? Number(stop.pointId) : null;

  let ownerType: string | null = null;
  let ownerName: string | null = null;
  let applicationNumber: string | null = null;

  const displayCity = resolveStructuredStopCity(stop, ctx);

  if (pointType === 'warehouse' && pointId != null) {
    ownerType = ROUTE_POINT_TYPE_LABELS.warehouse;
    ownerName =
      stop.warehouse?.name?.trim() ||
      resolveWarehouseName(ctx, pointId) ||
      (displayCity !== '—' ? displayCity : null) ||
      null;
  } else if (pointType === 'application' && pointId != null) {
    ownerType = ROUTE_POINT_TYPE_LABELS.application;
    const app = findApplication(ctx, pointId);
    applicationNumber = app?.internalNumber != null ? String(app.internalNumber).trim() : String(pointId);
    ownerName = app?.client?.name?.trim() || app?.consignee?.name?.trim() || null;
  } else if (pointType === 'consignee') {
    ownerType = ROUTE_POINT_TYPE_LABELS.consignee;
    ownerName =
      stop.contractor?.name?.trim() ||
      (pointId != null ? findApplication(ctx, pointId)?.consignee?.name?.trim() : null) ||
      ctx.routeConsigneeName?.trim() ||
      stop.contactPerson?.trim() ||
      null;
  } else if (pointType === 'shipper') {
    ownerType = ROUTE_POINT_TYPE_LABELS.shipper;
    ownerName = stop.contractor?.name?.trim() || stop.contactPerson?.trim() || null;
  }

  const stopKindLabel =
    stopType === 'loading' || stopType === 'unloading' ? ROUTE_STOP_KIND_LABELS[stopType] : 'Точка';
  let primaryLine = ownerType ?? stopKindLabel;
  let secondaryLine: string | null = ownerName;

  if (pointType === 'warehouse' && ownerName) {
    primaryLine = `${ROUTE_POINT_TYPE_LABELS.warehouse} ${ownerName}`;
    secondaryLine = null;
  } else if (pointType === 'application' && applicationNumber) {
    primaryLine = `Заявка №${applicationNumber}`;
    secondaryLine = ownerName;
  } else if (pointType === 'consignee') {
    primaryLine = ROUTE_POINT_TYPE_LABELS.consignee;
    secondaryLine = ownerName ?? (displayCity !== '—' ? displayCity : null);
  } else if (!ownerType && displayCity !== '—') {
    primaryLine = displayCity;
    secondaryLine = null;
  }

  const sourceLabel = secondaryLine ? `${primaryLine}\n${secondaryLine}` : primaryLine;
  const destinationLabel = primaryLine;

  return {
    stopType,
    pointType,
    ownerType,
    ownerName,
    applicationNumber,
    city: stop.city?.trim() || null,
    displayCity,
    destinationLabel,
    primaryLine,
    secondaryLine,
    sourceLabel
  };
}

export function formatRouteStopSourceDisplay(presentation: RouteStopPresentation): {
  primary: string;
  secondary: string | null;
} {
  return { primary: presentation.primaryLine, secondary: presentation.secondaryLine };
}

export function formatStopDestinationFromContext(params: {
  pointType?: string | null;
  warehouseName?: string | null;
  consigneeName?: string | null;
  applicationLabel?: string | null;
  clientName?: string | null;
  city?: string | null;
  address?: string | null;
}): string | null {
  const pt = normPointType(params.pointType);
  if (pt === 'warehouse') {
    const name = params.warehouseName?.trim();
    if (name) return `${ROUTE_POINT_TYPE_LABELS.warehouse} ${name}`;
    return ROUTE_POINT_TYPE_LABELS.warehouse;
  }
  if (pt === 'consignee') {
    const name = params.consigneeName?.trim();
    return name ? `${ROUTE_POINT_TYPE_LABELS.consignee} ${name}` : ROUTE_POINT_TYPE_LABELS.consignee;
  }
  if (pt === 'application') {
    if (params.applicationLabel) return params.applicationLabel;
    if (params.clientName) return `${ROUTE_POINT_TYPE_LABELS.application} ${params.clientName}`;
  }
  if (params.warehouseName) return `${ROUTE_POINT_TYPE_LABELS.warehouse} ${params.warehouseName}`;
  if (params.consigneeName) return `${ROUTE_POINT_TYPE_LABELS.consignee} ${params.consigneeName}`;
  const fallback = displayCityWithAddressFallback(params.city, params.address);
  return fallback !== '—' ? fallback : null;
}

export function formatSharedUnloadPresentation(hint: {
  cargoCount: number;
  destinationLabel: string | null;
}): string {
  const dest = hint.destinationLabel?.trim() || 'общая точка выгрузки';
  const cargoPhrase =
    hint.cargoCount === 1
      ? '1 груз'
      : hint.cargoCount >= 2 && hint.cargoCount <= 4
        ? `${hint.cargoCount} груза`
        : `${hint.cargoCount} грузов`;
  return `Общая выгрузка · ${cargoPhrase} · ${dest}`;
}
